import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getAbhaStatus, getUhrStatus, connectAbha, connectUhr } from '@/services/api';
import { toast } from 'sonner';
import { Link2 } from 'lucide-react';

export default function IntegrationsPage() {
  const [abhaStatus, setAbhaStatus] = useState({ configured: false });
  const [uhrStatus, setUhrStatus] = useState({ configured: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const [abha, uhr] = await Promise.all([getAbhaStatus(), getUhrStatus()]);
      setAbhaStatus(abha);
      setUhrStatus(uhr);
    } catch (error) {
      toast.error('Unable to load integration status');
    }
  };

  const handleConnect = async (type) => {
    setLoading(true);
    try {
      if (type === 'abha') {
        await connectAbha();
        toast.success('ABHA connection initiated');
      } else {
        await connectUhr();
        toast.success('UHR connection initiated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Integration not configured yet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="integrations-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Health Data Integrations
          </h1>
          <p className="text-slate-500">Connect government health data sources as credentials become available.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="abha-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-900" /> ABHA (India)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Badge variant="secondary" data-testid="abha-status">
                {abhaStatus.configured ? 'Configured' : 'Credentials Required'}
              </Badge>
              <p className="text-sm text-slate-500">Connect ABHA to unify records from hospitals, labs, and pharmacies.</p>
              <Button
                className="bg-blue-900 hover:bg-blue-800 text-white"
                onClick={() => handleConnect('abha')}
                disabled={!abhaStatus.configured || loading}
                data-testid="abha-connect-btn"
              >
                {abhaStatus.configured ? 'Connect ABHA' : 'Awaiting Credentials'}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm" data-testid="uhr-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-900" /> UHR (Ayushman Bharat)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Badge variant="secondary" data-testid="uhr-status">
                {uhrStatus.configured ? 'Configured' : 'Credentials Required'}
              </Badge>
              <p className="text-sm text-slate-500">Connect UHR to sync Ayushman Bharat digital health records.</p>
              <Button
                className="bg-blue-900 hover:bg-blue-800 text-white"
                onClick={() => handleConnect('uhr')}
                disabled={!uhrStatus.configured || loading}
                data-testid="uhr-connect-btn"
              >
                {uhrStatus.configured ? 'Connect UHR' : 'Awaiting Credentials'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}